/** Server-side permission vocabulary and deliberately small role mapping. */
export const PERMISSIONS = [
  "platform.read", "platform.manage", "platform.shell", "cluster.read", "cluster.manage", "cluster.dataplane.manage",
  "site.read", "site.create", "site.deploy", "site.update", "site.delete", "site.shell",
  "domain.read", "domain.manage", "dns.read", "dns.manage",
  "database.read", "database.create", "database.manage", "database.user.manage", "database.delete", "database.query",
  "cache.read", "cache.manage", "cache.cli", "cache.flush", "storage.read", "storage.manage",
  "mail.read", "mail.manage", "backup.read", "backup.create", "backup.restore", "backup.delete",
  "security.read", "security.manage", "security.scan", "user.read", "user.manage",
  "crowdsec.read", "crowdsec.decisions.manage", "crowdsec.allowlists.manage", "crowdsec.rules.manage", "crowdsec.appsec.manage",
  "crowdsec.install", "crowdsec.adopt", "crowdsec.upgrade", "crowdsec.repair", "crowdsec.uninstall", "crowdsec.profiles.manage",
  "account.read", "account.manage", "audit.read", "logs.read", "notification.read", "notification.manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];
export type AccountRole = "OWNER" | "ADMIN" | "DEVELOPER" | "DNS_ADMIN" | "DATABASE_ADMIN" | "VIEWER" | "MEMBER";

const readPermissions = PERMISSIONS.filter((permission) => permission.endsWith(".read") && !/^(platform|cluster|crowdsec)\./.test(permission));
const accountAll = PERMISSIONS.filter((permission) => !permission.startsWith("platform.") && !permission.startsWith("cluster.") && !permission.startsWith("crowdsec."));

export const ROLE_PERMISSIONS: Record<AccountRole, readonly Permission[]> = {
  OWNER: accountAll,
  ADMIN: accountAll.filter((permission) => !["account.manage", "security.manage", "user.manage"].includes(permission)),
  DEVELOPER: ["site.read", "site.deploy", "site.update", "site.shell", "database.read", "database.query", "cache.read", "logs.read", "notification.read"],
  DNS_ADMIN: ["domain.read", "domain.manage", "dns.read", "dns.manage", "notification.read"],
  DATABASE_ADMIN: ["database.read", "database.create", "database.manage", "database.user.manage", "database.query", "notification.read"],
  VIEWER: readPermissions,
  MEMBER: ["account.read", "site.read", "logs.read", "mail.read", "mail.manage", "dns.read", "dns.manage", "database.read", "database.query", "domain.read", "notification.read"],
};

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}

export function isAccountPermission(value: string): value is Permission {
  return isPermission(value) && !/^(platform|cluster|crowdsec)\./.test(value);
}

export function permissionsForMembership(role: string, explicitPermissions: string | null | undefined): Set<Permission> {
  const mapped = Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, role) ? ROLE_PERMISSIONS[role as AccountRole] : [];
  const { allow, deny } = parsePermissionOverrides(explicitPermissions);
  return new Set([...mapped, ...allow].filter((permission) => isAccountPermission(permission) && !deny.includes(permission)));
}

/** Legacy grant arrays remain valid; versioned overrides add explicit denials. */
export function parsePermissionOverrides(value: string | null | undefined): { allow: Permission[]; deny: Permission[] } {
  try {
    const parsed: unknown = JSON.parse(value || "[]");
    const valid = (items: unknown): Permission[] => Array.isArray(items) ? items.filter((item): item is Permission => typeof item === "string" && isPermission(item)) : [];
    if (Array.isArray(parsed)) return { allow: valid(parsed), deny: [] };
    if (parsed && typeof parsed === "object") {
      const overrides = parsed as { allow?: unknown; deny?: unknown };
      return { allow: valid(overrides.allow), deny: valid(overrides.deny) };
    }
  } catch { /* Invalid stored values never add grants. */ }
  return { allow: [], deny: [] };
}
